import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  Menu,
  CircularProgress,
  Pagination,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { AppDispatch, RootState } from "../../stores";
import {
  getAllLeads,
  getLeadsConversion,
  getLeadStats,
  getPieChartLeads,
  Lead,
  updateLeadStatus,
} from "../../features/lead-module/leadAction";
import { clearLeadMessages } from "../../features/lead-module/leadSlice";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  "& .MuiTableRow-root": {
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(1, 0.5),
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(75, 91, 93, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(1),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  padding: theme.spacing(1.2, 0.5),
  lineHeight: "1.2rem",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    backgroundColor: "#ede7d9",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const StatusPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 2,
  padding: "5px 10px",
  fontSize: "0.75rem",
  fontWeight: 600,
  cursor: "pointer",
  userSelect: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    filter: "brightness(0.98)",
    transform: "translateY(-1px)",
  },
});

const PaginationContainer = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "300px",
  flexGrow: 1,
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const statusOptions = [
  { value: "new_lead", label: "New Lead" },
  { value: "tracking", label: "Tracking" },
  { value: "follow_up", label: "Follow Up" },
  { value: "closed", label: "Closed" },
];

const STATUS_STYLES: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  new_lead: { bg: "#FFF4E5", color: "#A05A00", border: "#FFD8A8" },
  tracking: { bg: "#E7F5FF", color: "#0B7285", border: "#A5D8FF" },
  follow_up: { bg: "#F3F0FF", color: "#5F3DC4", border: "#D0BFFF" },
  closed: { bg: "#E6FCF5", color: "#087F5B", border: "#96F2D7" },
};

interface TableLead {
  no: number;
  name: string;
  contact: string;
  email: string;
  note: string;
  status: string;
  _id: string;
}

const LeadsTable: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const { leads, loading, error, pagination } = useSelector(
    (state: RootState) => state.lead
  );
  const [tableLeads, setTableLeads] = useState<TableLead[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Fetch leads on component mount and when pagination changes
  useEffect(() => {
    dispatch(getAllLeads({ page: currentPage, pageSize }));
    return () => {
      dispatch(clearLeadMessages());
    };
  }, [dispatch, currentPage, pageSize]);

  // Map API leads to table format
  useEffect(() => {
    const mappedLeads: TableLead[] = leads.map((lead: Lead, index: number) => ({
      no: (currentPage - 1) * pageSize + index + 1,
      name: lead.fullname,
      contact: lead.phoneNumber,
      email: lead.email,
      note: lead.description,
      status: lead.status || "new_lead",
      _id: lead._id,
    }));
    setTableLeads(mappedLeads);
  }, [leads, currentPage, pageSize]);

  // Handle status update locally and dispatch API call
  const handleStatusChange = (leadId: string, newStatus: string) => {
    dispatch(updateLeadStatus({ id: leadId, status: newStatus })).then(
      (action) => {
        if (updateLeadStatus.fulfilled.match(action)) {
          setTableLeads((prev) =>
            prev.map((lead) =>
              lead._id === leadId ? { ...lead, status: newStatus } : lead
            )
          );
          dispatch(getLeadStats());
          dispatch(getPieChartLeads({}));
          dispatch(getLeadsConversion());
        }
        setMenuAnchorEl(null);
        setActiveLeadId(null);
      }
    );
  };

  const openStatusMenu = (
    event: React.MouseEvent<HTMLElement>,
    leadId: string
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveLeadId(leadId);
  };

  const closeStatusMenu = () => {
    setMenuAnchorEl(null);
    setActiveLeadId(null);
  };

  const handleSelectStatus = (status: string) => {
    if (activeLeadId) {
      handleStatusChange(activeLeadId, status);
    }
    closeStatusMenu();
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} color="#4b5b5d">
          Leads Table
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: isMobile ? "center" : "flex-end",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Typography variant="body2" color="#4b5b5d">
            Show:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pageSize}
              onChange={(event) => handlePageSizeChange(event as any)}
              sx={{
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                  fontSize: "0.8rem",
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="#4b5b5d">
            entries
          </Typography>
        </Box>
      </Box>

      {/* Enhanced Loading State */}
      {loading && (
        <LoaderContainer>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: "#4b5b5d",
            }}
          />
          <LoaderText>Loading leads data...</LoaderText>
        </LoaderContainer>
      )}

      {/* Error State */}
      {!loading && error && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 2,
            flexGrow: 1,
            minHeight: "200px",
          }}
        >
          <Typography
            color="error"
            sx={{
              textAlign: "center",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Success State */}
      {!loading && !error && (
        <Box
          sx={{
            overflowX: "auto",
            "-webkit-overflow-scrolling": "touch",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#4b5b5d",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#354142",
            },
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: tableLeads.length === 0 ? "center" : "flex-start",
          }}
        >
          {tableLeads.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minHeight: "200px",
              }}
            >
              <Typography
                variant="h6"
                color="#4b5b5d"
                sx={{
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                No Leads Available
              </Typography>
            </Box>
          ) : (
            <StyledTable size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>No.</StyledTableHeadCell>
                  <StyledTableHeadCell>Lead Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Contact Details</StyledTableHeadCell>
                  <StyledTableHeadCell>Email</StyledTableHeadCell>
                  <StyledTableHeadCell>Note/Message</StyledTableHeadCell>
                  <StyledTableHeadCell>Status</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLeads.map((lead) => (
                  <TableRow key={lead._id}>
                    <StyledTableCell>{lead.no}.</StyledTableCell>
                    <StyledTableCell>{lead.name}</StyledTableCell>
                    <StyledTableCell>{lead.contact}</StyledTableCell>
                    <StyledTableCell>{lead.email}</StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title={truncateText(lead.note, 100)} arrow placement="top">
                        <span style={{ cursor: "default" }}>
                          {truncateText(lead.note, 35)}
                        </span>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell>
                      <StatusPill
                        onClick={(e) => openStatusMenu(e, lead._id)}
                        sx={{
                          backgroundColor: STATUS_STYLES[lead.status]?.bg,
                          color: STATUS_STYLES[lead.status]?.color,
                          border: `1px solid ${
                            STATUS_STYLES[lead.status]?.border
                          }`,
                        }}
                      >
                        {statusOptions.find((opt) => opt.value === lead.status)
                          ?.label || lead.status}
                        <ArrowDropDownIcon fontSize="small" />
                      </StatusPill>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          )}
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={
              activeLeadId
                ? tableLeads.find((l) => l._id === activeLeadId)?.status ===
                  option.value
                : false
            }
            onClick={() => handleSelectStatus(option.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                mr: 1,
                backgroundColor: STATUS_STYLES[option.value].color,
              }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Pagination Section */}
      {!loading && pagination && (
        <PaginationContainer>
          <Typography
            variant="body2"
            color="#4b5b5d"
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, pagination.totalLeads)} of{" "}
            {pagination.totalLeads} entries
          </Typography>

          {pagination.totalPages >= 0 && (
            <Pagination
              count={pagination.totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#f5f0e7",
                    color: "#4b5b5d",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#ede7d9",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          )}
        </PaginationContainer>
      )}
    </Container>
  );
};

export default LeadsTable;
